import { InfoCircleOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Divider,
  Empty,
  Input,
  Layout,
  Result,
  Skeleton,
  Table,
  notification,
  theme, Tag,
} from "antd";
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiAccountsList } from "../../../../api/accounts";
import { useIsMobileHook } from "../../../../utils/WindowHooks";
import "./Accounts.scss";

const { Content } = Layout;

const Accounts = () => {
  const isMobile = useIsMobileHook(1600);
  const navigate = useNavigate();
  const [dataSales, setDataSale] = useState([]);
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState(dataSales);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    pageSize: 10,
  });

  const handleRowClick = (record) => {
    const id = record.id;
    const newExpandedRowKeys = [...expandedRowKeys];
    const index = newExpandedRowKeys.indexOf(id);
    if (index > -1) {
      newExpandedRowKeys.splice(index, 1);
    } else {
      newExpandedRowKeys.push(id);
    }
    setExpandedRowKeys(newExpandedRowKeys);
  };

  const handleSearch = async (value) => {
    setSearchText(value);
    setLoading(true);
    try {
      if (value === '') {
        await listAccess(pagination.current);
      } else {
        const json = await apiAccountsList({ page: 1, search: value });
        setFilteredData(json.results);
        setPagination((prevPagination) => ({
          ...prevPagination,
          total: json.count,
          current: 1,
        }));
      }
    } catch (error) {
      notification.error({
        message: 'Erro',
        description: 'Ocorreu um erro ao buscar os dados.',
        placement: 'topRight',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = useCallback((value) => {
    setSearchText(value);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const newTimeout = setTimeout(() => {
      handleSearch(value);
    }, 500);
    
    setSearchTimeout(newTimeout);
  }, [searchTimeout, pagination.current]);

  const [loading, setLoading] = useState(true);

  const [activeError, setActiveError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");

  const renderError = (status) => {
    if (activeError) {
      return (
        <Result
          status={status === "500" ? "500" : "404"}
          title={status}
          subTitle={
            status === "500"
              ? "Ocorreu um erro no servidor, tente novamente mais tarde."
              : "Ocorreu um erro, tente novamente mais tarde."
          }
          extra={
            <Button onClick={() => navigate("/")} type="primary">
              Dashboard
            </Button>
          }
        />
      );
    }
  };

  const listAccess = async (page) => {
    setLoading(true);
    try {
      const json = await apiAccountsList({ page });
      setDataSale(json);
      setFilteredData(json.results);
      setPagination((prevPagination) => ({
        ...prevPagination,
        total: json.count,
        current: page,
      }));
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setActiveError(true);
      console.log(error)
      if (error.status === 400) {
        notification.error({
          message: "Erro",
          description: "Ocorreu um erro ao listar os clientes.",
          placement: "topRight",
        });
        setErrorStatus("404");
      } else if (error.status === 500) {
        notification.error({
          message: "Erro",
          description:
            "Ocorreu um erro no servidor ao listar os clientes. Por favor, tente novamente mais tarde.",
          placement: "topRight",
        });
        setErrorStatus("500");
      } else {
        setErrorStatus("404");
        notification.error("Erro ao buscar os clientes.");
      }
    }
  };

  const onChange = (pagination) => {
    listAccess(pagination.current);
  };

  useEffect(() => {
    if (dataSales?.length <= 1) {
      listAccess(pagination.current);
    }
  }, []);

  const accountTypeName = (type) => {
    if (type === "SELLER_PF") {
      return "PF";
    } else if (type === "SELLER_PJ") {
      return "PJ";
    } else if (type === "SUB_SELLER_PJ") {
      return <><Tag color="gold">SUB</Tag>PJ</>;
    } else if (type === "SUB_SELLER_PF") {
      return <><Tag color="gold">SUB</Tag>PF</>;
    }
    return "Desconhecido";
  }

  const statusTypeName = (type) => {
    if (type === "pending") {
      return <Tag color="gold">Pendente</Tag>;
    } else if (type === "enabled" || type === "active") {
      return <Tag color="green">Ativo</Tag>;
    } else if (type === "disabled") {
      return <Tag color="volcano">Desativado</Tag>;
    } else if (type === "denied") {
      return <Tag color="red">Negado</Tag>;
    }
    return "Desconhecido";
  }

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Empresa",
      dataIndex: "display_name",
      key: "display_name",
      render: (display_name) => (
        <p>{display_name}</p>
      ),
      responsive: ["sm"],
    },
    {
      title: "Tipo",
      dataIndex: "type",
      key: "type",
      render: (type) => (
        <p style={{ textTransform: "capitalize" }}>
          {accountTypeName(type)}
        </p>
      ),
      responsive: ["lg"],
    },
    {
      title: "Status",
      dataIndex: "zoop_internal_status",
      key: "zoop_internal_status",
      render: (type) => (
        <p style={{ textTransform: "capitalize" }}>
          {statusTypeName(type)}
        </p>
      ),
      responsive: ["lg"],
    },
    {
      title: "",
      dataIndex: "",
      render: (confirme) => (
        <Link to={`/lojista/detalhes/${confirme.id}`}>
          <InfoCircleOutlined style={{ cursor: "pointer", height: "100%" }} />{" "}
        </Link>
      ),
      key: "",
      responsive: ["sm"],
    },
  ];

  const expandedRowRender = (record) => (
    <div className="expandid">
      <span>
        Email:{" "}
        <p>{record.email}</p>
      </span>
      <span>
        Empresa:{" "}
        <p>{record.display_name}</p>
      </span>
      <span>
        Tipo:{" "}
        <p style={{ textTransform: "capitalize" }}>
          {accountTypeName(record.type)}
        </p>
      </span>
      <span>
        Status:{" "}
        <p style={{ textTransform: "capitalize" }}>
          {statusTypeName(record.zoop_internal_status)}
        </p>
      </span>
      <span style={{ marginTop: "10px" }}>
        Detalhes:{" "}
        <Link to={`/lojista/detalhes/${record.id}`}>
          <InfoCircleOutlined style={{ cursor: "pointer", height: "100%" }} />
        </Link>
      </span>
    </div>
  );

  const locale = {
    emptyText: <Empty description="Sem dados" />,
  };

  return (
    <div className="Accounts">
      {activeError ? (
        renderError(errorStatus)
      ) : (
        <>
          <Layout
            style={{
              padding: "1.25rem",
            }}
          >
            <Breadcrumb
              style={{
                margin: "16px 0",
              }}
            >
              <h2 className="title-page">Listagem de Clientes</h2>
              <Divider />
              <Breadcrumb.Item>Listagem de Clientes</Breadcrumb.Item>
              <Breadcrumb.Item>Clientes</Breadcrumb.Item>
            </Breadcrumb>
            <Content
              style={{
                padding: 24,
                margin: 0,
                minHeight: 280,
                background: colorBgContainer,
                borderRadius: borderRadiusLG,
              }}
            >
              <div className="box-divider">
                <h2 className="subtitle-page">Listagem de Clientes</h2>
              </div>
              <Divider />
              {/* TODO: Implementar busca */}
              <Input.Search
                placeholder='Pesquisar'
                allowClear
                enterButton
                value={searchText}
                onSearch={handleSearch}
                onChange={(e) => handleSearchChange(e.target.value)}
                style={{ marginBottom: 16, maxWidth: '200px' }}
              />
              {loading ? (
                <Skeleton active />
              ) : (
                <Table
                  columns={columns}
                  locale={locale}
                  dataSource={
                    filteredData &&
                    filteredData.map((item) => ({ ...item, key: item.id }))
                  }
                  pagination={{
                    current: pagination.current,
                    total: pagination.total,
                    showSizeChanger: false,
                  }}
                  expandable={{
                    expandedRowRender: isMobile ? expandedRowRender : undefined,
                    expandedRowKeys: expandedRowKeys,
                    onExpand: (_, record) => handleRowClick(record),
                    responsive: ["xl"],
                  }}
                  bordered
                  onChange={onChange}
                />
              )}
            </Content>
            <Divider />
          </Layout>
        </>
      )}
    </div>
  );
};

export default Accounts;
